import type { IUpdatePlaybookRequest } from 'src/services/api/playbooks/use-update-playbook';
import {
    EPlaybookTrigger,
    EPlaybookStepTypeGroup,
    type IPlaybookLeadFilterParams,
    type IPlaybookStep,
    type IPlaybookTrigger,
} from 'src/services/api/playbooks/types';

const minutesByTimeUnit = {
    minutes: 1,
    hours: 60,
    days: 24 * 60,
} as const;

export const getUpdatePlaybookTriggerParams = (
    trigger: IPlaybookTrigger
): NonNullable<IUpdatePlaybookRequest['trigger']> => {
    return {
        run_limit_per_lead: trigger.run_limit_per_lead,
    };
};

export const getUpdatePlaybookTrigger = (
    trigger: IPlaybookTrigger
): IPlaybookTrigger => {
    if (
        trigger.type === EPlaybookTrigger.LeadUnresponsive &&
        trigger.params
    ) {
        return {
            ...trigger,
            params: {
                ...trigger.params,
                time_value: Number(trigger.params.time_value),
            },
        };
    }

    if (
        trigger.type === EPlaybookTrigger.TourStarted &&
        trigger.params
    ) {
        return {
            ...trigger,
            params: {
                ...trigger.params,
                time_offset_value: Number(trigger.params.time_offset_value),
            },
        };
    }

    return trigger;
};

export const getUpdatePlaybookTimeBasedParams = (
    trigger: IPlaybookTrigger
): IPlaybookLeadFilterParams | null => {
    if (
        trigger.type === EPlaybookTrigger.LeadUnresponsive &&
        trigger.params
    ) {
        return {
            time_based_minutes: Number(trigger.params.time_value) * minutesByTimeUnit[trigger.params.time_unit],
        };
    }

    if (
        trigger.type === EPlaybookTrigger.TourStarted &&
        trigger.params
    ) {
        return {
            time_based_context: trigger.params.time_offset_type,
            time_based_minutes: Number(trigger.params.time_offset_value) * minutesByTimeUnit[trigger.params.time_offset_unit],
        };
    }

    return null;
};

const hasParams = (
    step: IPlaybookStep
): step is IPlaybookStep & { params: Record<string, unknown> } => {
    return 'params' in step && step.params != null && typeof step.params === 'object';
};

const backendPatchLeadFilterParamKeys = [
    'lead_stages',
    'message_languages',
    'property_id',
    'time_based_context',
    'time_based_minutes',
] as const;

const pickBackendPatchLeadFilterParams = (
    params: Record<string, unknown>
): Record<string, unknown> => {
    return Object.fromEntries(
        Object.entries(params).filter(([key]) =>
            backendPatchLeadFilterParamKeys.includes(key as typeof backendPatchLeadFilterParamKeys[number])
        )
    );
};

export const getUpdatePlaybookLeadFilterParams = (
    firstStep?: IPlaybookStep,
    trigger?: IPlaybookTrigger
): IPlaybookLeadFilterParams | null => {
    let currentStep = firstStep;
    const leadFilterParams: Record<string, unknown> = {
        ...(trigger ? getUpdatePlaybookTimeBasedParams(trigger) : undefined),
    };

    while (currentStep) {
        if (
            [
                EPlaybookStepTypeGroup.TriggerFilter,
                EPlaybookStepTypeGroup.Filter,
            ].includes(currentStep.type_group) &&
            hasParams(currentStep)
        ) {
            Object.assign(leadFilterParams, pickBackendPatchLeadFilterParams(currentStep.params));
        }

        currentStep = currentStep.children?.[0]?.step;
    }

    if (Object.keys(leadFilterParams).length === 0) {
        return null;
    }

    return leadFilterParams as IPlaybookLeadFilterParams;
};
